// Renders every frame offline (SVG -> canvas) and makes the MP4 in the browser with WebCodecs (H.264 + AAC).
// The finished video stays in the browser as a blob URL to watch and download; nothing is uploaded.
import { createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { lineAudioClips } from "../audio/timelineAudio";
import { renderClips, renderSoundtrack } from "../audio/voice/render";
import { frameAt, type DrawFrame } from "../engine/frame";
import { timelineFrameAt, imagesInRange } from "../engine/timelineFrame";
import { FPS, exportFrameCount, timeline } from "../engine/timeline";
import { preloadImages } from "../library/images";
import { getLibrary, voiceOf } from "../library/store";
import { shotMotion, type Episode } from "../model/episode";
import { exportFrames, isTimelineEpisode, type TimelineEpisode } from "../model/timeline";
import { FrameSvg } from "../render/FrameSvg";
import type { ExportSize } from "../model/exportSize";
import { audioUrl } from "./api";


async function rasterise(svg: string) {
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

export async function exportEpisode(ep: Episode | TimelineEpisode, size: ExportSize, onProgress: (p: number) => void, signal: { cancelled: boolean }) {
  await document.fonts.load("600 22px 'Bricolage Grotesque'");
  const lib = getLibrary();
  if (isTimelineEpisode(ep)) return exportTimeline(ep, size, onProgress, signal);
  // Frames are SVG in an <img>, which only draws images inlined as data: URLs.
  await preloadImages(lib, ep.shots.flatMap(s => [...(s.picture ? [s.picture.src] : []), ...(shotMotion(s)?.frames.map(f => f.src) ?? [])]));
  const tl = timeline(ep);
  // Soundtrack first: every line through its character's voice chain, rendered offline at the timeline's times.
  const audio = await renderSoundtrack(tl, exportFrameCount(tl) / FPS, s => audioUrl(ep.id, s.line.audio!), voiceOf(lib));
  return makeVideo(size, audio, exportFrameCount(tl), t => frameAt(tl, t), tl.total, onProgress, signal);
}

// A timeline episode: frames from timelineFrameAt, sound from the spoken lines at their starts.
async function exportTimeline(ep: TimelineEpisode, size: ExportSize, onProgress: (p: number) => void, signal: { cancelled: boolean }) {
  const lib = getLibrary();
  await preloadImages(lib, imagesInRange(ep, 0, Infinity));
  const frames = exportFrames(ep, FPS);
  const audio = await renderClips(lineAudioClips(ep, f => audioUrl(ep.id, f)), frames / FPS, voiceOf(lib));
  return makeVideo(size, audio, frames, t => timelineFrameAt(ep, t, t), Infinity, onProgress, signal);
}

async function drawFrame(ctx: CanvasRenderingContext2D, frame: DrawFrame, W: number, H: number) {
  const svg = renderToStaticMarkup(createElement(FrameSvg, { frame, id: "x", width: W, height: H }));
  ctx.drawImage(await rasterise(svg), 0, 0, W, H);
}

function newCanvas(W: number, H: number) {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  return { canvas, ctx: canvas.getContext("2d")! };
}

export const CANT_ENCODE = "This browser can't make videos. Try a recent Chrome, Edge or Safari.";

// The browser's own encoder: needs H.264 and AAC through WebCodecs.
async function makeVideo(size: ExportSize, audio: AudioBuffer, frames: number, frameAtTime: (t: number) => DrawFrame, lastT: number, onProgress: (p: number) => void, signal: { cancelled: boolean }) {
  const mb = await browserEncoder(size, audio);
  if (!mb) throw new Error(CANT_ENCODE);
  return encodeInBrowser(mb, size, audio, frames, frameAtTime, lastT, onProgress, signal);
}

async function browserEncoder(size: ExportSize, audio: AudioBuffer) {
  if (typeof VideoEncoder === "undefined" || typeof AudioEncoder === "undefined") return null;
  try {
    const mb = await import("mediabunny");
    const ok = (await mb.canEncodeVideo("avc", { width: size.w, height: size.h })) && (await mb.canEncodeAudio("aac", { numberOfChannels: audio.numberOfChannels, sampleRate: audio.sampleRate }));
    return ok ? mb : null;
  } catch {
    return null;
  }
}

async function encodeInBrowser(mb: typeof import("mediabunny"), size: ExportSize, audio: AudioBuffer, frames: number, frameAtTime: (t: number) => DrawFrame, lastT: number, onProgress: (p: number) => void, signal: { cancelled: boolean }) {
  const { w: W, h: H } = size;
  const { canvas, ctx } = newCanvas(W, H);
  const target = new mb.BufferTarget();
  const output = new mb.Output({ format: new mb.Mp4OutputFormat({ fastStart: "in-memory" }), target });
  const video = new mb.CanvasSource(canvas, { codec: "avc", bitrate: mb.QUALITY_HIGH });
  const sound = new mb.AudioBufferSource({ codec: "aac", bitrate: 160_000 });
  output.addVideoTrack(video, { frameRate: FPS });
  output.addAudioTrack(sound);
  await output.start();
  await sound.add(audio);
  sound.close();
  for (let f = 0; f < frames; f++) {
    if (signal.cancelled) {
      await output.cancel();
      throw new Error("Export cancelled");
    }
    await drawFrame(ctx, frameAtTime(Math.min(f / FPS, lastT)), W, H);
    await video.add(f / FPS, 1 / FPS);
    if (f % 6 === 5 || f === frames - 1) onProgress((f + 1) / frames);
  }
  video.close();
  await output.finalize();
  return URL.createObjectURL(new Blob([target.buffer!], { type: "video/mp4" }));
}
